import { Component, ViewChild, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';

import { Store, select } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, map, take, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import * as fromRootStore from '@apps/root/store';
import * as fromIts2Store from '@apps/its2/store';
import { WizardComponent } from '@ui/misc/wizard/wizard.component';
import { PopinComponentBase } from '@ui/misc/popin-modal/popin-component-base';
import { GuardModalService } from '@ui/misc/guard-modal/guard-modal.service';
import { IAuroraState } from '@apps/aurora.state';
import { Its2Constants } from '@apps/its2/its2.constants';
import { CreateService, Step1 } from '@apps/its2/models/create-service';
import { CatalogEntry } from '@apps/its2/models';
import { UploadFilesService } from '@customer/itsm/upload-files/upload-files.service';
import { OpenPopinOriginator } from '@common/enum/open-popin-originator.enum';
import { DashConstants } from '@apps/dash/dash.constants';
import { PopinModalRef } from '@ui/misc/popin-modal/popin-modal.ref';
import { AppConstants } from '@apps/app.constants';
import { LanguageService } from '@common/services';

@Component({
    selector: 'aa-create-service',
    templateUrl: './create-service.component.html',
    styleUrls: [
        '../request-create.scss',
        './create-service.component.scss'
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CreateServiceComponent extends PopinComponentBase implements OnInit, OnDestroy
{
    @ViewChild('wizard', { static: true }) wizard!: WizardComponent;
    private destroyator = new Subject();
    public isShown: boolean = true;
    public lastStep: boolean = false;

    constructor (
        guardModalService: GuardModalService,
        private translateService: TranslateService,
        private router: Router,
        private store: Store<IAuroraState>,
        private uploadFilesService: UploadFilesService,
        private languageService: LanguageService,
        popinModalRef: PopinModalRef,

    )
    {
        super(guardModalService);

        this.store.dispatch(
            fromIts2Store.Its2MiscActions.Its2RequestsServiceCreateStep1Entered({ currentLang: this.translateService.currentLang })
        );

        const popinData = popinModalRef.data ?? {};
        const originator = popinData[AppConstants.OpenPopinOriginator];
        this.openPopinOriginator = originator === undefined
            ? OpenPopinOriginator.ITS2_REQUESTS
            : Number(originator);
        const selectedCatalogGuid = popinData.catalogGuid;
        const normalizedCatalogGuid = typeof selectedCatalogGuid === 'string'
            ? selectedCatalogGuid.toLowerCase()
            : null;

        if (this.openPopinOriginator === OpenPopinOriginator.NO_ROUTE)
        {
            const lang = this.languageService.getShortItsmLanguage();

            this.store.dispatch(fromIts2Store.Its2ValuesActions.Its2RequestsValuesRequested({
                propertyName: `urgency${lang}`
            }));
            this.store.dispatch(fromIts2Store.Its2CatalogsActions.Its2CatalogsServiceRequested());

            if (selectedCatalogGuid)
            {
                this.store.select(fromIts2Store.getAllCatalogsService)
                    .pipe(
                        filter((catalog: CatalogEntry[]) => catalog?.length > 0),
                        map((catalog: CatalogEntry[]) =>
                            catalog.find((ce: CatalogEntry) =>
                                ce.catalogGuid?.toLowerCase() === normalizedCatalogGuid
                            )
                        ),
                        filter((catalogEntry): catalogEntry is CatalogEntry => !!catalogEntry),
                        take(1),
                        tap((catalogEntry: CatalogEntry) =>
                        {
                            const step1 = new Step1(this.translateService.currentLang);
                            step1.catalogEntry = catalogEntry;
                            step1.valid = false;

                            this.store.dispatch(
                                fromIts2Store.Its2MiscActions.Its2RequestsServiceCreateStep1Exited({ step1 })
                            );
                        })
                    )
                    .subscribe();
            }
        }
    }

    ngOnDestroy (): void
    {
        this.destroyator.next(null);
        this.destroyator.complete();
    }

    ngOnInit (): void
    {
        Promise.resolve(null).then(() => this.isShown = true);
    }

    public onSelectedStep = (step: number): void =>
    {
        this.lastStep = (step === (this.wizard.steps.length - 1));
    };

    public onNextStep = (): void =>
    {
        this.store.dispatch(
            fromRootStore.RootMiscStoreActions.RootWizard2NextButtonClicked()
        );
    };

    public onCreate = (): void =>
    {
        this.store.select(fromIts2Store.getCreateService)
            .pipe(
                take(1),
                tap((cs: CreateService) =>
                {
                    const createService: CreateService = new CreateService(this.translateService.currentLang, cs);

                    this.store.dispatch(
                        fromIts2Store.Its2RequestsActions.Its2RequestsCreateServiceRequested({ createService })
                    );
                    this.close();
                })
            )
            .subscribe();
    };

    onClosePopin = (): void =>
    {
        this.store
            .pipe(
                select(fromRootStore.getPopinFormDirtyStatus),
                take(1),
                tap((status: boolean) =>
                {
                    this.preventClosePopinForm(undefined, status)
                        .pipe(
                            take(1),
                            tap((dialogResult: boolean) =>
                            {
                                if (dialogResult)
                                {
                                    this.close();
                                    this.uploadFilesService.clearFiles();
                                }
                            })
                        )
                        .subscribe();
                })
            )
            .subscribe();
    };

    private close ()
    {
        this.store.dispatch(
            fromIts2Store.Its2MiscActions.Its2RequestsServiceCreatePopinCloseRequested()
        );
        this.store.dispatch(
            fromRootStore.RootMiscStoreActions.RootDirtyPopinFormSet({ dirty: false })
        );
        this.isShown = false;

        if (this.openPopinOriginator != OpenPopinOriginator.NO_ROUTE)
        {
            if (this.openPopinOriginator === OpenPopinOriginator.DASH_REQUESTS)
            {
                this.router.navigate(DashConstants.MainRoute());
            }
            else
            {
                this.router.navigate([`${Its2Constants.BaseUrl}/${Its2Constants.Requests}`]);
            }
        }
    }
}
